// frontend/src/pages/Hackathon.tsx

import { useEffect, useState } from 'react';
import { useFeatures } from '../hooks/useFeatures';
import { authService } from '../services/auth.service';
import { CodeEditorPanel } from '../components/code-editor/CodeEditorPanel';
import { TeamMembersBadge } from '../components/teams/TeamMembersBadge';
import { usePyodide } from '../hooks/usePyodide';
import '../styles/hackathon.css';

const API_BASE = (import.meta.env.PROD && (import.meta.env.VITE_API_BASE_URL === 'http://localhost:8080' || !import.meta.env.VITE_API_BASE_URL)) ? '' : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080');

interface HackathonItem {
  id: string;
  title: string;
  description: string;
  status: string;
  start_date: string;
  end_date: string;
}
interface TeamInfo {
  id: string;
  name: string;
  invite_code: string;
  leader_id: string;
}
interface LeaderboardRow {
  rank: number;
  teamName: string;
  codeQuality: number;
  creativity: number;
  design: number;
  total: number;
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await authService.getAccessToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(init?.headers ?? {}) },
  });
  return res.json();
}

export function HackathonPage() {
  const [hackathons, setHackathons] = useState<HackathonItem[]>([]);
  const [selected, setSelected] = useState<HackathonItem | null>(null);
  const [team, setTeam] = useState<TeamInfo | null>(null);
  const [isLeader, setIsLeader] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [code, setCode] = useState('# Takımının projesini buraya yaz\n');
  const [description, setDescription] = useState('');
  const [leaderboard, setLeaderboard] = useState<LeaderboardRow[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<{ feedback: string } | null>(null);

  const { isLoading: pyodideLoading, runPython } = usePyodide();
  const [runOutput, setRunOutput] = useState<string | null>(null);

  async function handleRun() {
    const r = await runPython(code);
    setRunOutput(r.output || r.error || '(çıktı yok)');
  }

  useEffect(() => {
    api<{ hackathons: HackathonItem[] }>('/api/hackathons').then((d) => setHackathons(d.hackathons ?? []));
    api<{ team: TeamInfo | null; isLeader: boolean }>('/api/teams/mine').then((d) => {
      setTeam(d.team);
      setIsLeader(d.isLeader);
    });
  }, []);

  function selectHackathon(h: HackathonItem) {
    setSelected(h);
    api<{ leaderboard: LeaderboardRow[] }>(`/api/hackathons/${h.id}/leaderboard`).then((d) =>
      setLeaderboard(d.leaderboard ?? [])
    );
  }

  async function createTeam() {
    if (!newTeamName.trim()) return;
    const t = await api<TeamInfo>('/api/teams', { method: 'POST', body: JSON.stringify({ name: newTeamName }) });
    setTeam(t);
    setIsLeader(true);
  }

  async function joinTeam() {
    if (!joinCode.trim()) return;
    await api('/api/teams/join', { method: 'POST', body: JSON.stringify({ code: joinCode }) });
    const d = await api<{ team: TeamInfo | null; isLeader: boolean }>('/api/teams/mine');
    setTeam(d.team);
    setIsLeader(d.isLeader);
  }

  async function handleSubmit() {
    if (!selected || !team) return;
    setIsSubmitting(true);
    setResult(null);
    try {
      const runResult = await runPython(code);
      const data = await api<{ evaluation: { feedback: string } }>(`/api/hackathons/${selected.id}/submit`, {
        method: 'POST',
        body: JSON.stringify({ teamId: team.id, code, description: description + `\n\nÇıktı: ${runResult.output}` }),
      });
      setResult(data.evaluation);
      selectHackathon(selected);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="hackathon-page">
      <header className="admin-page-header">
        <h1>Hackathon 🏆</h1>
        <p>Takımınla yarış, AI jürisi seni değerlendirsin</p>
      </header>

      {!team ? (
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 className="parent-section-title">Önce bir takıma katıl</h3>
          <div className="grid grid-2">
            <div>
              <label style={{ fontSize: 13, color: 'var(--gray)' }}>Yeni Takım Kur</label>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <input className="input" placeholder="Takım adı" value={newTeamName} onChange={(e) => setNewTeamName(e.target.value)} />
                <button className="lesson-btn primary sm" onClick={createTeam}>Kur</button>
              </div>
            </div>
            <div>
              <label style={{ fontSize: 13, color: 'var(--gray)' }}>Davet Koduyla Katıl</label>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <input className="input" placeholder="Kod" value={joinCode} onChange={(e) => setJoinCode(e.target.value)} />
                <button className="lesson-btn primary sm" onClick={joinTeam}>Katıl</button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="card" style={{ marginBottom: 24 }}>
          <strong>Takımın: {team.name}</strong> — Davet Kodu: <code>{team.invite_code}</code>
          {!isLeader && <p className="parent-empty-text">Yalnızca takım lideri gönderim yapabilir.</p>}
          <TeamMembersBadge teamId={team.id} />
        </div>
      )}

      <div className="admin-content-grid" style={{ marginBottom: 24 }}>
        {hackathons.map((h) => (
          <div key={h.id} className="project-card" onClick={() => selectHackathon(h)} style={{ cursor: 'pointer' }}>
            <div className="project-header">
              <div className="project-title">{h.title}</div>
              <span className="project-status completed">{h.status}</span>
            </div>
            <div className="project-desc">{h.description}</div>
          </div>
        ))}
      </div>

      {selected && (
        <>
          {isLeader && team && (
            <div className="grid grid-2" style={{ marginBottom: 24, alignItems: 'start' }}>
              <div>
                <CodeEditorPanel code={code} onChange={setCode} onRun={handleRun} onReset={() => setCode('')} isRunning={pyodideLoading} />
                {runOutput && (
                  <div className="card" style={{ marginTop: 12 }}>
                    <pre className="output-panel-stdout">{runOutput}</pre>
                  </div>
                )}
              </div>
              <div>
                <div className="card" style={{ marginBottom: 16 }}>
                  <h3 className="parent-section-title">Proje Açıklaması</h3>
                  <textarea className="input" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
                </div>
                <button className="lesson-btn primary full" onClick={handleSubmit} disabled={isSubmitting}>
                  {isSubmitting ? '⏳ Jüri değerlendiriyor...' : '🚀 Gönder'}
                </button>
                {result && (
                  <div className="card" style={{ marginTop: 16 }}>
                    <p>{result.feedback}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="card">
            <h3 className="parent-section-title">🏆 Liderlik Tablosu</h3>
            {leaderboard.length === 0 ? (
              <p className="parent-empty-text">Henüz gönderim yapılmadı.</p>
            ) : (
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead><tr><th>#</th><th>Takım</th><th>Kod</th><th>Yaratıcılık</th><th>Tasarım</th><th>Toplam</th></tr></thead>
                  <tbody>
                    {leaderboard.map((r) => (
                      <tr key={r.rank}>
                        <td>{r.rank}</td><td>{r.teamName}</td><td>{r.codeQuality}</td><td>{r.creativity}</td><td>{r.design}</td><td><strong>{r.total}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
